'use client';

import { useState } from 'react';

import { useParams } from 'next/navigation';

import { zodResolver } from '@hookform/resolvers/zod';
import { sendGAEvent } from '@next/third-parties/google';

import posthog from 'posthog-js';
import { useForm } from 'react-hook-form';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/form';
import Icon from '@repo/ui/icon';
import { Input } from '@repo/ui/input';
import { Textarea } from '@repo/ui/textarea';

import { toClassName } from 'helpers/format';

import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'hooks/use-translation';

import { contactFormSchema, TContactForm } from './contact-form.types';

import { styles } from '.';

const ContactForm = ({ gtmId }: { gtmId: string }) => {
  const [isPending, setIsPending] = useState(false);

  const params = useParams();

  const { t } = useTranslation(params?.locale as string);
  const { toast } = useToast();

  const defaultValues: TContactForm = {
    contactFirstName: '',
    contactLastName: '',
    contactEmailAddress: '',
    contactTelephoneNumber: '',
    contactMessage: '',
    projectLocation: '',
  };

  const form = useForm<TContactForm>({
    resolver: zodResolver(contactFormSchema),
    defaultValues,
  });

  const onSubmitWithoutServerActions = async () => {
    setIsPending(true);

    const values = form.getValues();

    posthog.capture('Contact Form Submit', values);

    sendGAEvent('event', 'conversion', {
      send_to: `${gtmId}/NuPoCIvP5u0bEJiQlZoo`,
      value: 1.0,
      currency: 'USD',
    });

    const response = await fetch('/api/submit-contact-form', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(values),
    });
    const result = await response.json();

    if (result.status === 'error') {
      console.error(result.errors);
      posthog.capture('Contact Form Error', result.errors);
    }

    form.reset();
    toast(result.message);
    setIsPending(false);
  };

  return (
    <div className={styles.container}>
      <Form {...form}>
        <form
          // With server actions
          // onSubmit={form.handleSubmit(onSubmit)}
          // Without server actions
          // onSubmit={(values) => onSubmitWithoutServerActions(values)}
          onSubmit={form.handleSubmit(onSubmitWithoutServerActions)}
          className={styles.content}
        >
          {/* Contact First name */}
          <FormField
            name="contactFirstName"
            control={form.control as any}
            render={({ field }) => (
              <FormItem className={styles.item}>
                <FormLabel className={styles.label}>
                  {t('component.form.label.contactFirstName')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t(
                      'component.form.placeholder.contactFirstName',
                    )}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  {/* <FormDescription className={styles.description}>
                    {t('component.form.description.contactFirstName')}
                  </FormDescription> */}
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Contact Last name */}
          <FormField
            name="contactLastName"
            control={form.control as any}
            render={({ field }) => (
              <FormItem className={styles.item}>
                <FormLabel className={styles.label}>
                  {t('component.form.label.contactLastName')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t(
                      'component.form.placeholder.contactLastName',
                    )}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  {/* <FormDescription className={styles.description}>
                    {t('component.form.description.contactLastName')}
                  </FormDescription> */}
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Contact Email address */}
          <FormField
            name="contactEmailAddress"
            control={form.control as any}
            render={({ field }) => (
              <FormItem className={styles.item}>
                <FormLabel className={styles.label}>
                  {t('component.form.label.contactEmailAddress')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t(
                      'component.form.placeholder.contactEmailAddress',
                    )}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  {/* <FormDescription className={styles.description}>
                    {t('component.form.description.contactEmailAddress')}
                  </FormDescription> */}
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Contact Telephone number */}
          <FormField
            name="contactTelephoneNumber"
            control={form.control as any}
            render={({ field }) => (
              <FormItem className={styles.item}>
                <FormLabel className={styles.label}>
                  {t('component.form.label.contactTelephoneNumber')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="tel"
                    placeholder={t(
                      'component.form.placeholder.contactTelephoneNumber',
                    )}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  {/* <FormDescription className={styles.description}>
                    {t('component.form.description.contactTelephoneNumber')}
                  </FormDescription> */}
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Project location */}
          <FormField
            name="projectLocation"
            control={form.control as any}
            render={({ field }) => (
              <FormItem className={styles.item}>
                <FormLabel className={styles.label}>
                  {t('component.form.label.projectLocation')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t(
                      'component.form.placeholder.projectLocation',
                    )}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  {/* <FormDescription className={styles.description}>
                    {t('component.form.description.contactFirstName')}
                  </FormDescription> */}
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Contact Message */}
          <FormField
            name="contactMessage"
            control={form.control as any}
            render={({ field }) => (
              <FormItem className={styles.item}>
                <FormLabel className={styles.label}>
                  {t('component.form.label.contactMessage')}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t('component.form.placeholder.contactMessage')}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  {/* <FormDescription className={styles.description}>
                    {t('component.form.description.contactMessage')}
                  </FormDescription> */}
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Submit button */}
          <button
            // disabled={isPending}
            className={styles.submit.button}
            type="submit"
          >
            {isPending ? (
              <div className={styles.submit.content}>
                <Icon
                  accessor="spinner"
                  size="12px"
                  className={toClassName(styles.submit.icon, 'animate-spin')}
                />
                <span>{t('label.data.isSubmitting')}</span>
              </div>
            ) : (
              <div className={styles.submit.content}>
                <Icon
                  accessor="paper-plane"
                  size="12px"
                  className={styles.submit.icon}
                />
                <p className="">{t('label.data.submit')}</p>
              </div>
            )}
          </button>
        </form>
      </Form>
    </div>
  );
};

export default ContactForm;
